import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  IconButton,
  Divider,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import apiClient from '../utils/apiInterceptor';

const FeedbackManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [detailReplyContent, setDetailReplyContent] = useState('');
  const [statusForm, setStatusForm] = useState({
    status: '',
    priority: '',
    adminNotes: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: '',
    search: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // 获取反馈统计
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await apiClient.get('/kOjtx3AXNmCsT5Wz/feedback/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('获取统计失败:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // 获取反馈列表
  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...filters
      };
      // 移除空值
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await apiClient.get('/kOjtx3AXNmCsT5Wz/feedback', { params });
      if (response.data.success) {
        setFeedbacks(response.data.feedbacks || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      console.error('获取反馈列表失败:', error);
      showMessage('获取反馈列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFeedbacks();
  }, [page]);

  // 当筛选条件改变时重新获取
  useEffect(() => {
    if (page === 1) {
      fetchFeedbacks();
    } else {
      setPage(1);
    }
  }, [filters.status, filters.type, filters.priority, filters.search]);

  // 打开回复对话框
  const handleOpenReply = async (feedback) => {
    setSelectedFeedback(feedback);
    setReplyContent('');
    setOpenReplyDialog(true);
    // 获取完整的反馈详情（包含回复列表）
    try {
      const response = await apiClient.get(`/kOjtx3AXNmCsT5Wz/feedback/${feedback._id}`);
      if (response.data.success) {
        setSelectedFeedback(response.data.feedback);
      }
    } catch (error) {
      console.error('获取反馈详情失败:', error);
    }
  };

  // 提交回复
  const handleReply = async () => {
    if (!replyContent.trim()) {
      showMessage('请输入回复内容', 'error');
      return;
    }

    if (!selectedFeedback) {
      return;
    }

    // 检查反馈状态
    if (selectedFeedback.status === 'closed') {
      showMessage('已关闭的反馈不能回复', 'error');
      return;
    }

    try {
      const response = await apiClient.post(
        `/kOjtx3AXNmCsT5Wz/feedback/${selectedFeedback._id}/reply`,
        { content: replyContent.trim() }
      );
      if (response.data.success) {
        showMessage('回复成功', 'success');
        // 直接使用返回的更新后的反馈数据
        setSelectedFeedback(response.data.feedback);
        // 清空回复内容
        setReplyContent('');
        // 刷新列表
        fetchFeedbacks();
        fetchStats();
      }
    } catch (error) {
      console.error('回复失败:', error);
      showMessage(error.response?.data?.message || '回复失败', 'error');
    }
  };

  // 打开状态更新对话框
  const handleOpenStatus = (feedback) => {
    setSelectedFeedback(feedback);
    setStatusForm({
      status: feedback.status,
      priority: feedback.priority || 'medium',
      adminNotes: feedback.adminNotes || ''
    });
    setOpenStatusDialog(true);
  };

  // 更新状态
  const handleUpdateStatus = async () => {
    try {
      const response = await apiClient.put(
        `/kOjtx3AXNmCsT5Wz/feedback/${selectedFeedback._id}/status`,
        statusForm
      );
      if (response.data.success) {
        showMessage('状态更新成功', 'success');
        setOpenStatusDialog(false);
        fetchFeedbacks();
        fetchStats();
      }
    } catch (error) {
      console.error('更新状态失败:', error);
      showMessage(error.response?.data?.message || '更新状态失败', 'error');
    }
  };

  // 查看详情
  const handleViewDetail = async (id) => {
    try {
      const response = await apiClient.get(`/kOjtx3AXNmCsT5Wz/feedback/${id}`);
      if (response.data.success) {
        setSelectedFeedback(response.data.feedback);
        setDetailReplyContent('');
        setOpenDetailDialog(true);
        // 刷新列表以更新未读标记
        fetchFeedbacks();
      }
    } catch (error) {
      console.error('获取详情失败:', error);
      showMessage('获取详情失败', 'error');
    }
  };

  // 在详情对话框中回复
  const handleDetailReply = async () => {
    if (!detailReplyContent.trim()) {
      showMessage('请输入回复内容', 'error');
      return;
    }

    if (!selectedFeedback) {
      return;
    }

    // 检查反馈状态
    if (selectedFeedback.status === 'closed') {
      showMessage('已关闭的反馈不能回复', 'error');
      return;
    }

    try {
      const response = await apiClient.post(
        `/kOjtx3AXNmCsT5Wz/feedback/${selectedFeedback._id}/reply`,
        { content: detailReplyContent.trim() }
      );
      if (response.data.success) {
        showMessage('回复成功', 'success');
        // 直接使用返回的更新后的反馈数据
        setSelectedFeedback(response.data.feedback);
        // 清空回复内容
        setDetailReplyContent('');
        // 刷新列表
        fetchFeedbacks();
        fetchStats();
      }
    } catch (error) {
      console.error('回复失败:', error);
      showMessage(error.response?.data?.message || '回复失败', 'error');
    }
  };

  // 删除反馈
  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条反馈吗？')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/kOjtx3AXNmCsT5Wz/feedback/${id}`);
      if (response.data.success) {
        showMessage('删除成功', 'success');
        fetchFeedbacks();
        fetchStats();
      }
    } catch (error) {
      console.error('删除失败:', error);
      showMessage(error.response?.data?.message || '删除失败', 'error');
    }
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      processing: 'info',
      resolved: 'success',
      closed: 'default'
    };
    return colors[status] || 'default';
  };

  // 获取状态文本
  const getStatusText = (status) => {
    const texts = {
      pending: '待处理',
      processing: '处理中',
      resolved: '已解决',
      closed: '已关闭'
    };
    return texts[status] || status;
  };

  // 获取优先级颜色
  const getPriorityColor = (priority) => {
    const colors = {
      low: 'default',
      medium: 'info',
      high: 'warning',
      urgent: 'error'
    };
    return colors[priority] || 'default';
  };

  // 获取优先级文本
  const getPriorityText = (priority) => {
    const texts = {
      low: '低',
      medium: '中',
      high: '高',
      urgent: '紧急'
    };
    return texts[priority] || priority;
  };

  // 获取类型图标
  const getTypeIcon = (type) => {
    const icons = {
      bug: <BugReportIcon />,
      feature: <LightbulbIcon />,
      suggestion: <FeedbackIcon />,
      complaint: <ReportProblemIcon />,
      other: <HelpOutlineIcon />
    };
    return icons[type] || <HelpOutlineIcon />;
  };

  // 获取类型文本
  const getTypeText = (type) => {
    const texts = {
      bug: '错误报告',
      feature: '功能建议',
      suggestion: '改进建议',
      complaint: '投诉',
      other: '其他'
    };
    return texts[type] || type;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* 统计卡片 */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'rgba(0, 30, 60, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" sx={{ color: '#00ff9f', fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                总反馈数
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'rgba(0, 30, 60, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                {stats.pending}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                待处理
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'rgba(0, 30, 60, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                {stats.processing}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                处理中
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'rgba(0, 30, 60, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                textAlign: 'center'
              }}
            >
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                {stats.resolved}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                已解决
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* 筛选和搜索 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'rgba(0, 30, 60, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>状态</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="状态"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="pending">待处理</MenuItem>
                <MenuItem value="processing">处理中</MenuItem>
                <MenuItem value="resolved">已解决</MenuItem>
                <MenuItem value="closed">已关闭</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>类型</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                label="类型"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="bug">错误报告</MenuItem>
                <MenuItem value="feature">功能建议</MenuItem>
                <MenuItem value="suggestion">改进建议</MenuItem>
                <MenuItem value="complaint">投诉</MenuItem>
                <MenuItem value="other">其他</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>优先级</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                label="优先级"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="low">低</MenuItem>
                <MenuItem value="medium">中</MenuItem>
                <MenuItem value="high">高</MenuItem>
                <MenuItem value="urgent">紧急</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="搜索标题、内容、用户名..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1 }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* 反馈列表 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: 'rgba(0, 30, 60, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#00ff9f' }} />
          </Box>
        ) : feedbacks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              暂无反馈记录
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>标题</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>用户</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>类型</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>状态</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>优先级</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>回复数</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>创建时间</TableCell>
                    <TableCell sx={{ color: '#00ff9f', fontWeight: 'bold' }}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow
                      key={feedback._id}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(0, 255, 159, 0.05)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#fff',
                              cursor: 'pointer',
                              '&:hover': { color: '#00ff9f' }
                            }}
                            onClick={() => handleViewDetail(feedback._id)}
                          >
                            {feedback.title}
                          </Typography>
                          {feedback.adminRead === false && (
                            <Badge
                              badgeContent="新"
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.6rem',
                                  height: '16px',
                                  minWidth: '16px',
                                  padding: '0 4px'
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {feedback.username || feedback.userId?.username || '未知'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTypeText(feedback.type)}
                          size="small"
                          icon={getTypeIcon(feedback.type)}
                          sx={{
                            bgcolor: 'rgba(0, 255, 159, 0.1)',
                            color: '#00ff9f'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusText(feedback.status)}
                          size="small"
                          color={getStatusColor(feedback.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPriorityText(feedback.priority)}
                          size="small"
                          color={getPriorityColor(feedback.priority)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {feedback.replies?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          {new Date(feedback.createdAt).toLocaleString('zh-CN')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Tooltip title="回复">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenReply(feedback)}
                              sx={{ color: '#00ff9f', p: isMobile ? 0.5 : 1 }}
                            >
                              <SendIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="更新状态">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenStatus(feedback)}
                              sx={{ color: '#2196f3', p: isMobile ? 0.5 : 1 }}
                            >
                              <EditIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="删除">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(feedback._id)}
                              sx={{ color: '#f44336' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* 分页 */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#fff',
                      '&.Mui-selected': {
                        bgcolor: '#00ff9f',
                        color: '#001e3c'
                      }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* 回复对话框 */}
      <Dialog
        open={openReplyDialog}
        onClose={() => {
          setOpenReplyDialog(false);
          setReplyContent('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh'
          }
        }}
      >
        {selectedFeedback && (
          <>
            <DialogTitle sx={{ color: '#00ff9f' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">回复反馈: {selectedFeedback.title}</Typography>
                <IconButton
                  onClick={() => {
                    setOpenReplyDialog(false);
                    setReplyContent('');
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
              <Box sx={{ mt: 1 }}>
                {/* 反馈基本信息 */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={getTypeText(selectedFeedback.type)}
                    size="small"
                    icon={getTypeIcon(selectedFeedback.type)}
                    sx={{
                      bgcolor: 'rgba(0, 255, 159, 0.1)',
                      color: '#00ff9f'
                    }}
                  />
                  <Chip
                    label={getStatusText(selectedFeedback.status)}
                    size="small"
                    color={getStatusColor(selectedFeedback.status)}
                  />
                  <Chip
                    label={getPriorityText(selectedFeedback.priority)}
                    size="small"
                    color={getPriorityColor(selectedFeedback.priority)}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                  用户: {selectedFeedback.username || selectedFeedback.userId?.username || '未知'}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 3, whiteSpace: 'pre-wrap' }}>
                  {selectedFeedback.content}
                </Typography>
                
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
                
                {/* 回复列表 */}
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ color: '#00ff9f', mb: 2 }}>
                      回复 ({selectedFeedback.replies.length})
                    </Typography>
                    {selectedFeedback.replies.map((reply, index) => {
                      const isLastReply = index === selectedFeedback.replies.length - 1;
                      const isUnreadUserReply = !reply.isAdmin && isLastReply && selectedFeedback.adminRead === false;
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: reply.isAdmin 
                              ? 'rgba(0, 255, 159, 0.05)' 
                              : 'rgba(100, 181, 246, 0.05)',
                            borderRadius: 1,
                            border: isUnreadUserReply 
                              ? '2px solid #ff9800' 
                              : '1px solid rgba(255, 255, 255, 0.1)',
                            position: 'relative'
                          }}
                        >
                          {isUnreadUserReply && (
                            <Chip
                              label="新消息"
                              size="small"
                              color="warning"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pr: isUnreadUserReply ? 8 : 0 }}>
                            <Typography variant="subtitle2" sx={{ color: reply.isAdmin ? '#00ff9f' : '#64b5f6' }}>
                              {reply.isAdmin ? '管理员' : reply.repliedByUsername}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              {new Date(reply.repliedAt).toLocaleString('zh-CN')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#fff', whiteSpace: 'pre-wrap' }}>
                            {reply.content}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* 回复输入框 */}
                {selectedFeedback.status !== 'closed' && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#00ff9f', mb: 1 }}>
                        添加回复
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="输入回复内容..."
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        InputProps={{
                          sx: {
                            color: '#fff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenReplyDialog(false);
                  setReplyContent('');
                }}
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                取消
              </Button>
              {selectedFeedback.status !== 'closed' && (
                <Button
                  onClick={handleReply}
                  variant="contained"
                  disabled={!replyContent.trim()}
                  sx={{
                    bgcolor: '#00ff9f',
                    color: '#001e3c',
                    '&:hover': {
                      bgcolor: '#00cc7f'
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(0, 255, 159, 0.3)',
                      color: 'rgba(0, 30, 60, 0.5)'
                    }
                  }}
                >
                  发送
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 状态更新对话框 */}
      <Dialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ color: '#00ff9f' }}>
          更新反馈状态: {selectedFeedback?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>状态</InputLabel>
              <Select
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                label="状态"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <MenuItem value="pending">待处理</MenuItem>
                <MenuItem value="processing">处理中</MenuItem>
                <MenuItem value="resolved">已解决</MenuItem>
                <MenuItem value="closed">已关闭</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>优先级</InputLabel>
              <Select
                value={statusForm.priority}
                onChange={(e) => setStatusForm({ ...statusForm, priority: e.target.value })}
                label="优先级"
                sx={{
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                <MenuItem value="low">低</MenuItem>
                <MenuItem value="medium">中</MenuItem>
                <MenuItem value="high">高</MenuItem>
                <MenuItem value="urgent">紧急</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="管理员备注（仅管理员可见）"
              value={statusForm.adminNotes}
              onChange={(e) => setStatusForm({ ...statusForm, adminNotes: e.target.value })}
              InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
              InputProps={{
                sx: {
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenStatusDialog(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            取消
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            sx={{
              bgcolor: '#00ff9f',
              color: '#001e3c',
              '&:hover': {
                bgcolor: '#00cc7f'
              }
            }}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>

      {/* 详情对话框 */}
      <Dialog
        open={openDetailDialog}
        onClose={() => {
          setOpenDetailDialog(false);
          setSelectedFeedback(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        {selectedFeedback && (
          <>
            <DialogTitle sx={{ color: '#00ff9f' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{selectedFeedback.title}</Typography>
                <IconButton
                  onClick={() => {
                    setOpenDetailDialog(false);
                    setSelectedFeedback(null);
                  }}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={getTypeText(selectedFeedback.type)}
                    size="small"
                    icon={getTypeIcon(selectedFeedback.type)}
                    sx={{
                      bgcolor: 'rgba(0, 255, 159, 0.1)',
                      color: '#00ff9f'
                    }}
                  />
                  <Chip
                    label={getStatusText(selectedFeedback.status)}
                    size="small"
                    color={getStatusColor(selectedFeedback.status)}
                  />
                  <Chip
                    label={getPriorityText(selectedFeedback.priority)}
                    size="small"
                    color={getPriorityColor(selectedFeedback.priority)}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                  用户: {selectedFeedback.username || selectedFeedback.userId?.username || '未知'}
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 3, whiteSpace: 'pre-wrap' }}>
                  {selectedFeedback.content}
                </Typography>
                {selectedFeedback.adminNotes && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
                    <Typography variant="subtitle2" sx={{ color: '#00ff9f', mb: 1 }}>
                      管理员备注
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#fff', mb: 3, whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.adminNotes}
                    </Typography>
                  </>
                )}
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />
                
                {/* 回复列表 */}
                {selectedFeedback.replies && selectedFeedback.replies.length > 0 && (
                  <Box>
                    <Typography variant="h6" sx={{ color: '#00ff9f', mb: 2 }}>
                      回复 ({selectedFeedback.replies.length})
                    </Typography>
                    {selectedFeedback.replies.map((reply, index) => {
                      // 判断是否是未读消息（用户回复且是最后一条）
                      const isLastReply = index === selectedFeedback.replies.length - 1;
                      const isUnreadUserReply = !reply.isAdmin && isLastReply && selectedFeedback.adminRead === false;
                      
                      return (
                        <Box
                          key={index}
                          sx={{
                            mb: 2,
                            p: 2,
                            bgcolor: reply.isAdmin 
                              ? 'rgba(0, 255, 159, 0.05)' 
                              : 'rgba(100, 181, 246, 0.05)',
                            borderRadius: 1,
                            border: isUnreadUserReply 
                              ? '2px solid #ff9800' 
                              : '1px solid rgba(255, 255, 255, 0.1)',
                            position: 'relative'
                          }}
                        >
                          {isUnreadUserReply && (
                            <Chip
                              label="新消息"
                              size="small"
                              color="warning"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                fontSize: '0.7rem',
                                height: '20px'
                              }}
                            />
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pr: isUnreadUserReply ? 8 : 0 }}>
                            <Typography variant="subtitle2" sx={{ color: reply.isAdmin ? '#00ff9f' : '#64b5f6' }}>
                              {reply.isAdmin ? '管理员' : reply.repliedByUsername}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              {new Date(reply.repliedAt).toLocaleString('zh-CN')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#fff', whiteSpace: 'pre-wrap' }}>
                            {reply.content}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* 回复输入框 */}
                {selectedFeedback.status !== 'closed' && (
                  <>
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 3 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#00ff9f', mb: 1 }}>
                        添加回复
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="输入回复内容..."
                        value={detailReplyContent}
                        onChange={(e) => setDetailReplyContent(e.target.value)}
                        InputProps={{
                          sx: {
                            color: '#fff',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.3)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.5)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00ff9f'
                            }
                          }
                        }}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          onClick={handleDetailReply}
                          variant="contained"
                          startIcon={<SendIcon />}
                          disabled={!detailReplyContent.trim()}
                          sx={{
                            bgcolor: '#00ff9f',
                            color: '#001e3c',
                            '&:hover': {
                              bgcolor: '#00cc7f'
                            },
                            '&:disabled': {
                              bgcolor: 'rgba(0, 255, 159, 0.3)',
                              color: 'rgba(255, 255, 255, 0.5)'
                            }
                          }}
                        >
                          发送回复
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDetailDialog(false);
                  setSelectedFeedback(null);
                  setDetailReplyContent('');
                }}
                sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
              >
                关闭
              </Button>
              <Button
                onClick={() => {
                  setOpenDetailDialog(false);
                  handleOpenStatus(selectedFeedback);
                }}
                variant="outlined"
                sx={{
                  borderColor: '#2196f3',
                  color: '#2196f3',
                  '&:hover': {
                    borderColor: '#1976d2',
                    bgcolor: 'rgba(33, 150, 243, 0.1)'
                  }
                }}
              >
                更新状态
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 消息提示 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FeedbackManagement;

